import {
  findNoteTarget,
  toParentOffset,
  toScreen,
} from '@components/ArtBoard/StickyNotes'
import { STICKY_NOTES_COLORS } from '@components/ArtBoard/StickyNotes/constants'
import {
  Factory,
  type ID,
  type Offset,
  ProjectActions,
  type StickyNote,
} from '@octopus-do/octopus-project-core'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import { migrationCanvasSelectors } from '@store/migrationSelectors/canvasSelectors'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { getCloneId, getId, randomShift } from '@store/utils'
import { commitProjectRequest } from '@storeOld/actionCreators'
import * as ActionTypes from '@storeOld/actions'

import centerElement from '@/utils/centerElement'

export const thunkAddStickyNote = (): AppThunk => (dispatch) => {
  const id = getId()

  const offset: Offset = centerElement(0, 0, [
    '.ExternalLink-container',
    '.StickyNote-container',
  ])

  const stickyNote = Factory.stickyNote({
    id,
    offset,
    color: STICKY_NOTES_COLORS[0],
    size: {
      height: 135,
      width: 135,
    },
  })

  dispatch(thunkPutStickyNote(stickyNote))
}

// The note comes with an offset on the tab. When the middle of the screen is
// over a page or a tree section, the note hangs on that instead (OCT-5256),
// re-measured from its corner so it shows up in the same spot.
const anchorAtScreenCenter = (
  stickyNote: StickyNote,
  tabId: ID,
  scale: number
): [ID, StickyNote] => {
  const targetId = findNoteTarget({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  })
  const screen = targetId && toScreen(stickyNote.offset, tabId, scale)
  const offset = targetId && screen && toParentOffset(screen, targetId, scale)

  if (!targetId || !offset) {
    return [tabId, stickyNote]
  }

  return [targetId, { ...stickyNote, offset }]
}

export const thunkPutStickyNote =
  (stickyNote: StickyNote): AppThunk =>
  (dispatch, getState) => {
    const activeTab = migrationProjectSelector.getActiveOrFirstTabId(getState())
    const scale = migrationCanvasSelectors.getScale(getState())

    const [parentId, item] = anchorAtScreenCenter(stickyNote, activeTab, scale)

    dispatch(
      commitProjectRequest(
        ProjectActions.stickyNotes.add({
          items: [item],
          index: 0,
          parents: [parentId],
        })
      )
    )
  }

export const duplicateStickyNote =
  ({ id }: { id: ID }): AppThunk =>
  (dispatch, getState) => {
    if (!id) {
      return
    }

    const { offset } = migrationProjectSelector.getProjectStickyNoteById(id)(
      getState()
    )

    const initiator = getId()

    const clonedId = getCloneId(id, initiator)

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ProjectActions.stickyNotes.clone({
          id,
          initiator,
        }),
        ProjectActions.stickyNotes.updateOffset({
          id: clonedId,
          offset: randomShift(offset),
        }),
      ],
    })
  }

export const thunkDeleteStickyNote = (): AppThunk => (dispatch, getState) => {
  const { canvas } = getState()

  const id = canvas.stickyNote.selectedStickyNote

  if (!id) {
    return
  }

  dispatch({
    type: ActionTypes.COMMIT_PROJECT_REQUEST,
    payload: [ProjectActions.stickyNotes.delete({ ids: [id] })],
  })
}
